import React, { useMemo, useState } from "react";
import { Box, Switch, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import { PhysicalSessionDraft, SessionStepProps } from "../types";
import { RoomListItem, useRoomsList } from "../hooks/useLocations";

const PAGE_SIZE = 10;

const RoomStep: React.FC<SessionStepProps<PhysicalSessionDraft>> = ({
  data,
  setData,
  errors,
  corporateId,
  disabledFields,
  confirmedDisabledReason,
}) => {
  const { t } = useTranslation("session-wizard");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { items, totalItems, totalPages, isLoading } = useRoomsList({
    corporateId,
    siteId: data.site?.id,
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
  });

  const selectedIds = useMemo(
    () => new Set<string | number>(data.rooms.map((r) => String(r.id))),
    [data.rooms],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (next) {
      const found = items.find((r) => String(r.id) === String(id));
      if (!found) return;
      setData({
        rooms: [...data.rooms, { id, label: found.name || String(id) }],
      });
    } else {
      setData({
        rooms: data.rooms.filter((r) => String(r.id) !== String(id)),
      });
    }
  };

  if (!data.site) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          fontSize: "13px",
          color: theme.palette.text.tertiary,
        }}
      >
        {t("room.no-site")}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {errors.rooms && (
        <Typography sx={{ fontSize: "12px", color: theme.palette.error.main }}>
          {errors.rooms}
        </Typography>
      )}
      {disabledFields.has("rooms") ? (
        <Box
          sx={{
            padding: "12px 16px",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            fontSize: "14px",
            color: theme.palette.text.primary,
          }}
        >
          {data.rooms.map((r) => r.label).join(", ") || "-"}
        </Box>
      ) : (
        <SearchableMultiSelectListCst<RoomListItem>
          items={items}
          getId={(r) => String(r.id)}
          renderRow={({ item }) => (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {item.name || `#${item.id}`}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: theme.palette.text.tertiary,
                }}
              >
                {[
                  `ID: ${item.id}`,
                  item.code,
                  item.max_capacity != null
                    ? t("room.capacity", { count: item.max_capacity })
                    : null,
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </Typography>
            </Box>
          )}
          selectedIds={selectedIds}
          onToggle={handleToggle}
          initialSearchValue={search}
          onSearchSubmit={(v) => {
            setSearch(v);
            setPage(1);
          }}
          searchPlaceholder={String(t("room.search-placeholder"))}
          searchButtonLabel={String(t("people-picker.search-button"))}
          page={page}
          pageSize={PAGE_SIZE}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
          isLoading={isLoading}
          selectedCountLabel={String(
            t("room.selected", { count: data.rooms.length }),
          )}
          emptyLabel={String(t("room.empty"))}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mt: 1,
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {t("room.qr-code")}
          </Typography>
          <Switch
            checked={data.qrCode}
            disabled={disabledFields.has("QR-code")}
            onChange={(e) => setData({ qrCode: e.target.checked })}
          />
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {t("room.confirmed")}
            </Typography>
            <Switch
              checked={data.confirmed}
              disabled={disabledFields.has("confirmed")}
              onChange={(e) => setData({ confirmed: e.target.checked })}
            />
          </Box>
          <Typography
            sx={{
              fontSize: "11px",
              color:
                disabledFields.has("confirmed") && confirmedDisabledReason
                  ? theme.palette.warning.main
                  : theme.palette.text.tertiary,
            }}
          >
            {disabledFields.has("confirmed") && confirmedDisabledReason
              ? confirmedDisabledReason
              : t("room.confirmed-hint")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RoomStep;
